import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@NA/Overrides/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductCarouselProductStyles } from "@insite/content-library/Components/ProductCarouselProduct";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ADIProductCarouselProduct from "@NA/Components/ProductDetail/ADIProductCarouselProduct";
import ADIColors from "@NA/Utils/ADIColors";
import { useEmblaCarousel } from "embla-carousel/react";
import * as React from "react";
import { connect, ResolveThunks, useSelector } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
import ADIRecentlyViewedProductGridCard from "@NA/Widgets/ADIRecentlyViewedProductGridCard";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import loadRecentlyViewedProducts from "@NA/Handlers/ADIRecentlyViewedProducts";
import { useState} from "react";
const enum fields {
    title = "title",
    carouselType = "carouselType",
    displayProductsFrom = "displayProductsFrom",
    selectedCategoryIds = "selectedCategoryIds",
    relatedProductType = "relatedProductType",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    seedWithManuallyAssigned = "seedWithManuallyAssigned",
    maxNumberOfColumns = "maxNumberOfColumns",
    showImage = "showImage",
    showTitle = "showTitle",
    showAvailability = "showAvailability",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    showBrand = "showBrand",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.carouselType]: string;
        [fields.showBrand]: boolean;
        [fields.displayProductsFrom]: string;
        [fields.selectedCategoryIds]: string[];
        [fields.relatedProductType]: string;
        [fields.showAvailability]: boolean;
        [fields.numberOfProductsToDisplay]: number;
        [fields.seedWithManuallyAssigned]: string;
        [fields.maxNumberOfColumns]: number;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
    };
}

const mapStateToProps = (state: CustomApplicationState, ownProps: OwnProps) => ({
    products: getProductsForProductInfoList(state, ownProps.id),
    errorMessage: getErrorMessage(state, ownProps.id),
    pageType: getCurrentPage(state).type,
    recentlyViewedData: state.ADIGlobalData.recentlyViewedStore.recentlyViewedProducts?.recentlyViewedProducts,
    recentlyViewedIsLoading: state.ADIGlobalData.recentlyViewedStore.isLoading,
});

const mapDispatchToProps = {
    loadCarouselProducts,
    loadRecentlyViewedProducts,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface ADIRecentlyViewedStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    recentlyViewedContainer?: InjectableCss;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    cartCarouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    cartCarouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    addToCartContainer?: InjectableCss;
}

export const adiRecentlyViewedStyles: ADIRecentlyViewedStyles = {
    addToCartContainer: {
        css: css`
            @media screen and (max-width: 767px) {
                margin-left: -66%;
                padding-top: 0px;
                width: 166%;
                > div {
                    button {
                        justify-content: center;
                        height: 64px;
                    }
                }
            }
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: start;
            margin-bottom: 10px;
        `,
    },
    recentlyViewedContainer: {
        css: css`
            display: inline-block;
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            position: relative;
            margin: auto;
            > ${GridOffset} {
                ${({ theme }) => { return breakpointMediaQueries(
                    theme, [null, null, null, css`
                    margin: 0 -10px;
                    width: calc(100% + 20px);
                    max-width: calc(1480px + 10px);
                    `,],"min",); 
                }}
            }
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                        position: absolute;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            position: absolute;
            top: 8%;
            right: 50px;
            transform: rotate(180deg);
            background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/newicons/arrow-carousel-next-rd.svg)
                no-repeat;
            border: 0;
            width: 28px !important;
            height: 28px !important;
            opacity: 1;
            margin-bottom: 0;
            background-size: 28px 28px !important;
            z-index: 9;
            right: auto;
            top: 36%;
            left: 10px;
            &:disabled {
                background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/ArrowCarouselright-2.svg)
                    no-repeat;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        top: 34%;
                        left: 10px;
                        min-height: 28px;
                        //display: none;
                    `,
                    css`
                        top: 34%;
                        left: 10px;
                        min-height: 28px;
                    `,
                    css`
                        top: 34%;
                        left: 10px;
                        min-height: 28px;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                ])}
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                        position: absolute;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            position: absolute;
            top: 29%;
            right: 0px;
            background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/newicons/arrow-carousel-next-rd.svg)
                no-repeat;
            border: 0;
            width: 28px !important;
            height: 28px !important;
            opacity: 1;
            margin-bottom: 0;
            background-size: 28px 28px !important;
            z-index: 9;
            left: auto;
            top: 36%;
            right: 10px;
            &:disabled {
                background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/ArrowCarouselright-2.svg)
                    no-repeat;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        top: 34%;
                        right: 10px;
                        min-height: 28px;
                        //display: none;
                    `,
                    css`
                        top: 34%;
                        right: 10px;
                        min-height: 28px;
                    `,
                    css`
                        top: 34%;
                        right: 10px;
                        min-height: 28px;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                ])}
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css` padding: 0px; width: calc(100% + 20px); margin-left: -10px;`,
                    css` padding: 0px; width: calc(100% + 20px); margin-left: -10px;`,
                    css` padding: 0px; width: calc(100% + 20px); margin-left: -10px;`,
                    null,
                    null,
                ])}
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            flex-basis: 20%;
            max-width: 20%;
            padding: 0 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 33.333%;
                        max-width: 33.333%;
                    `,
                    css`
                        flex-basis: 20%;
                        max-width: 20%;
                    `,
                    css`
                        flex-basis: 20%;
                        max-width: 20%;
                    `,
                ])}
        `,
    },
    cartCarouselSlide: {
        css: css`
            padding: 15px !important;
            position: relative;
            flex: 0 0 auto;
            flex-basis: 20%;
            max-width: 20%;
            padding: 0 10px;
            display: inline-flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        width: 100%;
                    `,
                    css`
                        flex-basis: 33.333%;
                        max-width: 33.333%;
                        flex-basis: 100%;
                        max-width: 100%;
                        width: 100%;
                    `,
                    css`
                        flex-basis: 20%;
                        max-width: 20%;
                    `,
                    css`
                        flex-basis: 20%;
                        max-width: 20%;
                    `,
                ])}
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0px;
            display: flex;
            flex-direction: column;
            border: 1px solid ${ADIColors.gray7};
            border-radius: 5px;
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                        align-items: center;
                        flex-flow: row;
                        height: auto;
                    `,
                    css`
                        display: flex;
                        align-items: center;
                        flex-flow: row;
                        height: auto;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    cartCarouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0px;
            display: flex;
            flex-direction: column;
            border-radius: 5px;
            display: flex;
            border: 1px solid rgb(211, 211, 211);
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                        align-items: flex-start;
                        padding-top: 10px;
                        flex-flow: row;
                    `,
                    css`
                        display: flex;
                        align-items: flex-start;
                        padding-top: 10px;
                        flex-flow: row;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: 24px;
            position: absolute;
            width: 100%;
            display: none!important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 93%;
                    `,
                    null,
                    null,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                ])}
        `,
    },
    dotButton: {
        css: css`
            min-height: 7px;
            max-width: 55px;
            height: 3px;
            width: 10%;
            padding: 0;
            margin: 0 4px;
            border: 0px;
            border-radius: 2px;
            background: ${getColor("common.border")};
            background-color: ${ADIColors.gray16};
            border: 1px solid ${ADIColors.gray16};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0 0;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            &:hover {
                background: ${ADIColors.white};
            }
            &:active {
                background-color: ${ADIColors.darkBlue1};
                border: 1px solid ${ADIColors.darkBlue1};
            }
            &:focus {
                outline: none;
            }
        `,
    },
};

const styles = adiRecentlyViewedStyles;

const ADIRecentlyViewed: React.FC<Props> = React.memo(({
    id,
    theme,
    fields,
    products,
    recentlyViewedData,
    errorMessage,
    loadCarouselProducts,
    loadRecentlyViewedProducts,
    pageType,
    shellContext,
    recentlyViewedIsLoading,
}) => {
    const afterCarousel = React.createRef<HTMLSpanElement>();
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = React.useContext(BrandStateContext).value;
    const category = React.useContext(CategoryContext);
    const productContext = React.useContext(ProductContext);
    const [slideProducts, setSildeProduct] = React.useState<ProductModel[]>();
    const [selectedIndex, setSelectedIndex] = React.useState(0);

    const [emblaRef, embla] = useEmblaCarousel({ loop: false });
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(true);
        const getCustomerCreditStatus = useSelector(  
        (state: CustomApplicationState) => state.ADIGlobalData.PricingStatusStore,
    );
  
    const [accountPendingVerification_RecentlyViewProduct, setaccountPendingVerification_RecentlyViewProduct] = useState<string| null >("");
    React.useEffect(()=>{
        setaccountPendingVerification_RecentlyViewProduct(getCustomerCreditStatus?.getcustomerStatus?.creditStatus)
    },[accountPendingVerification_RecentlyViewProduct,getCustomerCreditStatus?.getcustomerStatus?.creditStatus])

    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        if (embla) {
            setSelectedIndex(embla.selectedScrollSnap());
        }
    };

    const getSlidesToScroll = React.useCallback(() => {
        const maxNumberOfColumns = fields.maxNumberOfColumns || 4;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 2;
        } else {
            localSlidesToScroll = 5;
        }

        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [fields.maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[3];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [fields.maxNumberOfColumns, windowResizeTime]);

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();
        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (pageType === "ProductDetailsPage" && !recentlyViewedIsLoading) {
            loadRecentlyViewedProducts({
                productErpNumber: productContext.product.productNumber,
                pageType: "productdetailview",
            });
        } else if (!recentlyViewedIsLoading) {
            loadRecentlyViewedProducts({
                productErpNumber:
                    productContext && productContext.product ? `${productContext.product?.productNumber}` : "",
                pageType: "shoppingcartview",
            });
        }
    }, []);

    React.useEffect(() => {
        if (!embla || !recentlyViewedData || recentlyViewedData.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            draggable: isTouchDevice(),
            loop: recentlyViewedData.length > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, recentlyViewedData]);

    React.useEffect(() => {
        if (recentlyViewedData && recentlyViewedData.length > 0) {
            const newProductArray: ProductModel[] = [];
            const itreate = slidesToScroll;
            let nextItertor = itreate;
            newProductArray.push(recentlyViewedData[0]);
            recentlyViewedData.forEach((item, index) => {
                if (index === nextItertor) {
                    newProductArray.push(item);
                    nextItertor = nextItertor + itreate;
                }
            });
            setSildeProduct([...newProductArray]);
        }
    }, [recentlyViewedData]);
    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    if (!recentlyViewedData || recentlyViewedData.length === 0) {
        return null;
    }

    const title = fields.title;

    const onClickDot = (i: number) => {
        if (embla) {
            embla.scrollTo(i);
            setSelectedIndex(i);
        }
    };

    const scrollPrev = () => {
        if (embla) {
            if (selectedIndex !== 0) {
                if (selectedIndex - 1 === 0) {
                    setCanScrollPrev(false);
                } else {
                    setCanScrollPrev(true);
                }
                setCanScrollNext(true);
                setSelectedIndex(prev => prev - 1);
                embla && embla.scrollPrev();
            }
        }
    };

    const scrollNext = () => {
        if (embla && slideProducts) {
            if (selectedIndex < slideProducts?.length - 1) {
                if (selectedIndex < slideProducts?.length - 1 && selectedIndex + 1 === slideProducts?.length - 1) {
                    setCanScrollNext(false);
                } else {
                    setCanScrollNext(true);
                }
                setCanScrollPrev(true);
                setSelectedIndex(prev => prev + 1);
                embla && embla.scrollNext();
            }
        }
    };

    return (
        <StyledWrapper className="RecentlyViewed_widget">
            {pageType === "ProductDetailsPage" ? (
                <StyledWrapper
                    css={css`                       
                       padding-bottom: 50px;
                       background-color: #f7f7f7;
                       position: relative;
                       @media only screen and (max-width: 768px) {
                            background-color: #fff;                            
                        } 
                        @media only screen and (min-width: 1081px) {  
                            padding-left: 15px;
                        }
                    `}
                >
                    <SkipNav
                        text={translate("Skip Carousel")}
                        extendedStyles={styles.skipCarouselButton}
                        destination={afterCarousel}
                    />
                    <StyledWrapper
                        css={css`
                            display: flex;
                            position: relative;
                            justify-content: flex-start;
                            padding-top: 34px;
                            max-width: 1440px;
                            margin: 0 auto;
                            position: initial;
                            @media only screen and (max-width: 768px) {
                                padding-top: 0px;
                            }
                        `}
                    >
                    <Typography css={css`
                       font-size:24px;
                       color: #002856;
                       font-weight: bold;
                       text-align: start;
                       margin-bottom: 10px;
                       @media only screen and (max-width: 768px) {
                            font-size:24px;
                            line-height: 1.38;
                        }
                    `}
                    >{title}</Typography>
                        {recentlyViewedData.length > slidesToScroll && (
                            <Button
                                {...styles.prevArrowButton}
                                onClick={() => scrollPrev()}
                                disabled={!canScrollPrev}
                                data-test-selector="prevBtn"
                            ></Button>
                        )}

                        {recentlyViewedData.length > slidesToScroll && (
                            <Button
                                {...styles.nextArrowButton}
                                onClick={() => scrollNext()}
                                disabled={!canScrollNext}
                                data-test-selector="nextBtn"
                            ></Button>
                        )}
                    </StyledWrapper>

                    <GridContainer
                        {...styles.mainContainer}
                        data-test-selector={`productCarousel_${fields.carouselType}${fields.relatedProductType}`}
                    >
                        <GridItem {...styles.carouselGridItem}>
                            <StyledWrapper {...styles.carouselWrapper}>
                                <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                    <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                        {recentlyViewedData.map(product => (
                                            <StyledWrapper
                                                {...styles.carouselSlide}
                                                key={product.id}
                                                style={{ width: `calc(100% / ${slidesToScroll})` }}
                                            >
                                                <StyledWrapper
                                                    {...styles.carouselSlideInner}
                                                    id={`product_${product.id}`}
                                                    data-test-selector="productContainer"
                                                >
                                                    <ADIRecentlyViewedProductGridCard
                                                        key={product.id}
                                                        product={product}
                                                        view="Grid"
                                                        showBrand={fields.showBrand}
                                                        showPartNumbers={fields.showPartNumbers}
                                                        showPrice={fields.showPrice}
                                                        showAvailability={fields.showAvailability}
                                                        carouselId={id}
                                                        PendingAccountverificationCreditStatus={accountPendingVerification_RecentlyViewProduct}
                                                        
                                                    />
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        ))}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                    </GridContainer>
                    <span ref={afterCarousel} tabIndex={-1} />
                    <StyledWrapper {...styles.dotsContainerWrapper}>
                        {slideProducts &&
                            slideProducts.map((_, i) => {
                                const dotColorStyles =
                                    i === selectedIndex
                                        ? `
                                            background:  ${ADIColors.darkBlue1};
                                            &:hover { background-color: ${ADIColors.darkBlue1};
                                                border: 1px solid ${ADIColors.darkBlue1}; }
                                            &:active {background-color: ${ADIColors.darkBlue1};
                                                border: 1px solid ${ADIColors.darkBlue1}; }
                                        `
                                        : "";
                                const dotButtonStyles = {
                                    css: css`
                                        ${styles.dotButton?.css || ""}
                                        ${dotColorStyles}
                                    `,
                                };
                                // eslint-disable-next-line react/no-array-index-key
                                return <Button key={i} {...dotButtonStyles} onClick={() => onClickDot(i)} />;
                            })}
                    </StyledWrapper>
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.recentlyViewedContainer}>
                    <Typography {...styles.titleText}>{title}</Typography>
                    {recentlyViewedData.map(product => (
                        <StyledWrapper {...styles.cartCarouselSlide} key={product.id}>
                            <StyledWrapper
                                {...styles.cartCarouselSlideInner}
                                id={`product_${product.id}`}
                                data-test-selector="productContainer"
                            >
                                <ADIRecentlyViewedProductGridCard
                                    key={product.id}
                                    product={product}
                                    view="Grid"
                                    showBrand={fields.showBrand}
                                    showPartNumbers={fields.showPartNumbers}
                                    showPrice={fields.showPrice}
                                    showAvailability={fields.showAvailability}
                                    carouselId={id}
                                    PendingAccountverificationCreditStatus={accountPendingVerification_RecentlyViewProduct}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
});

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(ADIRecentlyViewed))),
    definition: {
        group: "Common",
        icon: "Carousel",
        displayName: "ADIRecentlyViewed",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.showBrand,
                displayName: "Show Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showAvailability,
                displayName: "Show Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.numberOfProductsToDisplay,
                displayName: "Number Of Products To Display",
                editorTemplate: "IntegerField",
                min: 1,
                max: 20,
                defaultValue: 10,
                fieldType: "General",
                isVisible: item =>
                    item.fields[fields.carouselType] === "customersAlsoPurchased" ||
                    item.fields[fields.carouselType] === "topSellers",
                sortOrder: 5,
            },
            {
                name: fields.maxNumberOfColumns,
                displayName: "Max Number Of Columns",
                editorTemplate: "IntegerField",
                min: 1,
                max: 5,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.showBrandName,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 11,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 12,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 13,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 14,
            },
        ],
    },
};

export default widgetModule;
